import React from 'react'
import Barcode from 'react-barcode'
import { Rnd } from 'react-rnd'

import { mmToPx, pxToMm, resolveBarcode } from 'helpers/barcode'
import { ELEMENT_TYPES, resolveElementValue, clampElement, snap } from './layoutModel'

// Yorliqning YAGONA chizuvchisi. Tahrirlash ham, chop etish ham shu komponentdan
// o'tadi -- shuning uchun ular bir xil chiqishi kafolatlangan, tasodif emas.
//
// Eski shablonlarda markup ikki marta yozilgan edi (biri ekranga, biri `d-none`
// ichida printerga), shuning uchun ular vaqt o'tib bir-biridan uzoqlashib ketgan.
// Bu yerda farq faqat bitta: tahrirlash rejimida element <Rnd> ga o'raladi.
// Ichki mazmun (renderContent) esa ikkalasida ham aynan bir xil.

function elementStyle(el) {
	return {
		left: `${el.x}mm`,
		top: `${el.y}mm`,
		width: `${el.w}mm`,
		height: `${el.h}mm`,
	}
}

// Aylantirilgan elementda mazmun o'lchamlari almashadi: 90° burilgan quti
// ichida matn qutining balandligi bo'ylab cho'ziladi.
function contentSize(el) {
	const rotated = el.rotate === 90 || el.rotate === 270
	return {
		widthMm: rotated ? el.h : el.w,
		heightMm: rotated ? el.w : el.h,
	}
}

const ALIGN_TO_FLEX = { left: 'flex-start', center: 'center', right: 'flex-end' }
const VALIGN_TO_FLEX = { top: 'flex-start', middle: 'center', bottom: 'flex-end' }

function TextContent({ el, value }) {
	// Ko'p qatorli matnni (mahsulot nomi) qatorlar soni bo'yicha kesish.
	const clamp = el.lines > 1
	return (
		<div
			className="tag-el__text"
			style={{
				fontSize: `${el.fontSize}mm`,
				lineHeight: 1.15,
				fontWeight: el.bold ? 700 : 400,
				fontStyle: el.italic ? 'italic' : 'normal',
				textDecoration: el.strike ? 'line-through' : 'none',
				textAlign: el.align,
				width: '100%',
				...(clamp
					? {
						display: '-webkit-box',
						WebkitLineClamp: el.lines,
						WebkitBoxOrient: 'vertical',
						overflow: 'hidden',
					}
					: {
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					}),
			}}
		>
			{value}
		</div>
	)
}

function BarcodeContent({ el, value, widthMm, heightMm }) {
	const options = resolveBarcode(value, widthMm)
	if (!options) return null

	// Shtrix kod qutiga aniq sig'ishi uchun: umumiy SVG balandligi = chiziqlar +
	// (raqam ko'rsatilsa) shrift + oraliq. Teskarisiga hisoblab, chiziq balandligini
	// topamiz. margin=0 -- chetdagi bo'sh joy modul hisobiga kiritilgan (barcode.js).
	const textMargin = el.showValue ? 1 : 0
	const fontSizePx = el.showValue ? mmToPx(el.fontSize) : 0
	const barHeightPx = Math.max(4, mmToPx(heightMm) - fontSizePx - textMargin)

	return (
		<Barcode
			value={String(value)}
			format={options.format}
			lastChar={options.lastChar}
			width={options.width}
			height={barHeightPx}
			displayValue={!!el.showValue}
			fontSize={fontSizePx}
			textMargin={textMargin}
			margin={0}
			background="transparent"
			fontOptions=""
		/>
	)
}

function ElementContent({ el, product, cashbox }) {
	const type = ELEMENT_TYPES[el.type]
	if (!type) return null

	const { widthMm, heightMm } = contentSize(el)
	const value = resolveElementValue(el, product, cashbox)

	let inner = null
	if (type.kind === 'barcode') {
		inner = <BarcodeContent el={el} value={value} widthMm={widthMm} heightMm={heightMm} />
	} else if (type.kind === 'image') {
		inner = value ? <img className="tag-el__img" src={value} alt="" /> : null
	} else if (type.kind === 'line') {
		inner = <div className="tag-el__line" />
	} else {
		inner = <TextContent el={el} value={value} />
	}

	return (
		<div
			className="tag-el__inner"
			style={{
				width: `${widthMm}mm`,
				height: `${heightMm}mm`,
				transform: el.rotate ? `rotate(${el.rotate}deg)` : undefined,
				justifyContent: ALIGN_TO_FLEX[el.align] || 'center',
				alignItems: VALIGN_TO_FLEX[el.valign] || 'center',
			}}
		>
			{inner}
		</div>
	)
}

function TagCanvas({
	layout,
	product,
	cashbox,
	edit = false,
	zoom = 1,
	selectedId = null,
	onSelect,
	onChange,
	className = '',
}) {
	const elements = layout.elements.filter((el) => el.visible)

	const canvasStyle = {
		width: `${layout.widthMm}mm`,
		height: `${layout.heightMm}mm`,
	}

	// --- CHOP ETISH / KO'RISH REJIMI -----------------------------------------
	// Hech qanday tahrirlash qatlami yo'q: sof divlar, mm o'lchamlarda.
	if (!edit) {
		return (
			<div className={`tag-canvas ${className}`} style={canvasStyle}>
				{elements.map((el) => (
					<div
						key={el.id}
						className={`tag-el ${el.border ? 'tag-el--border' : ''}`}
						style={elementStyle(el)}
					>
						<ElementContent el={el} product={product} cashbox={cashbox} />
					</div>
				))}
			</div>
		)
	}

	// --- TAHRIRLASH REJIMI ----------------------------------------------------
	// Rnd piksel bilan ishlaydi, model esa mm bilan. O'girish PX_PER_MM orqali
	// aniq (CSS spetsifikatsiyasi: 1mm = 96/25.4 px), yaxlitlash yo'qotishi yo'q.
	// Rnd ga `scale` berilgani muhim -- aks holda kattalashtirilgan kanvasda
	// sichqoncha harakati element harakatidan farq qiladi.
	const handleChange = (el, patch) => {
		onChange(clampElement({ ...el, ...patch }, layout))
	}

	return (
		<div className={`tag-canvas tag-canvas--edit ${className}`} style={canvasStyle}>
			{elements.map((el) => (
				<Rnd
					key={el.id}
					className={`tag-rnd ${selectedId === el.id ? 'tag-rnd--selected' : ''}`}
					bounds="parent"
					scale={zoom}
					size={{ width: mmToPx(el.w), height: mmToPx(el.h) }}
					position={{ x: mmToPx(el.x), y: mmToPx(el.y) }}
					onMouseDown={() => onSelect(el.id)}
					onDragStop={(e, d) => {
						handleChange(el, { x: snap(pxToMm(d.x)), y: snap(pxToMm(d.y)) })
					}}
					onResizeStop={(e, dir, ref, delta, position) => {
						handleChange(el, {
							w: snap(pxToMm(ref.offsetWidth)),
							h: snap(pxToMm(ref.offsetHeight)),
							x: snap(pxToMm(position.x)),
							y: snap(pxToMm(position.y)),
						})
					}}
				>
					<div className={`tag-el tag-el--edit ${el.border ? 'tag-el--border' : ''}`}>
						<ElementContent el={el} product={product} cashbox={cashbox} />
					</div>
				</Rnd>
			))}
		</div>
	)
}

// Shtrix kod elementining skanerlanish holatini tekshirish (konstruktor
// ogohlantirish ko'rsatishi uchun). Chizishdagi mantiqning aynan o'zi ishlatiladi.
export function checkBarcodeScannability(layout, product) {
	const el = layout.elements.find((e) => e.type === 'barcode' && e.visible)
	if (!el) return null
	const { widthMm } = contentSize(el)
	const value = resolveElementValue(el, product, {})
	const options = resolveBarcode(value, widthMm)
	if (!options) return null
	return {
		format: options.format,
		moduleMm: Math.round(options.moduleMm * 1000) / 1000,
		scannable: options.scannable,
	}
}

export default TagCanvas
